import { useCallback, useMemo } from 'react';
import { GestureResponderEvent } from 'react-native';

import useExitPartnership from '@rover/pages/src/bright-horizons/GlobalUI/hooks/useExitPartnership';
import useModalProviderContext from '@rover/pages/src/bright-horizons/HubPage/context/modalContext';
import type { BrightHorizonsAction } from '@rover/rsdk/src/apiClient/latest/model/brightHorizonsAction';
import { CloseUseCreditActionActionType } from '@rover/rsdk/src/apiClient/latest/model/closeUseCreditActionActionType';
import { ExitPartnershipBhActionActionType } from '@rover/rsdk/src/apiClient/latest/model/exitPartnershipBhActionActionType';
import { LinkActionActionType } from '@rover/rsdk/src/apiClient/latest/model/linkActionActionType';
import { LogoutBhActionActionType } from '@rover/rsdk/src/apiClient/latest/model/logoutBhActionActionType';
import { OpenOutOfCreditModalActionActionType } from '@rover/rsdk/src/apiClient/latest/model/openOutOfCreditModalActionActionType';
import { OpenUseCreditModalActionActionType } from '@rover/rsdk/src/apiClient/latest/model/openUseCreditModalActionActionType';
import { OpenWebviewActionActionType } from '@rover/rsdk/src/apiClient/latest/model/openWebviewActionActionType';
import { logout } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { isReactNative } from '@rover/rsdk/src/modules/Env/react';
import { withEventTracking } from '@rover/shared/js/utils/withEventTracking';
import Navigation from '@rover/utilities/navigation';

import { isLinkAction } from '../HubPage/utils';

type Props = {
  action?: BrightHorizonsAction;
};

type ActionButtonProps = {
  href?: string;
  hrefTarget?: '_blank';
  onClick?: (
    event?: GestureResponderEvent | React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => void;
};

export const useBrightHorizonsActionHandler = ({ action }: Props): ActionButtonProps => {
  const { openModal, closeModal } = useModalProviderContext();
  const { exitHandler } = useExitPartnership();

  const handleOnClickLink = useCallback(() => {
    if (isReactNative() && isLinkAction(action)) {
      closeModal();
      Navigation.pop();
      Navigation.openDeepLink(action.deeplink, true);
    }
  }, [action, closeModal]);

  const handleLogout = useCallback(() => {
    if (typeof window !== 'object') return;
    logout({
      nextLocation: window.location.pathname + window.location.search,
    });
  }, []);

  return useMemo(() => {
    if (!action) return {};
    switch (action.actionType) {
      case LinkActionActionType.link:
        return {
          href: !isReactNative() ? action.webUrl : undefined,
          hrefTarget: action.hrefTarget === '_blank' ? '_blank' : undefined,
          onClick: withEventTracking(action.event, handleOnClickLink),
        };
      case OpenWebviewActionActionType.open_webview:
        return {
          href: action.webUrl,
          hrefTarget: '_blank',
          onClick: withEventTracking(action.event),
        };
      case OpenOutOfCreditModalActionActionType.open_out_of_credits:
      case OpenUseCreditModalActionActionType.open_use_credits:
        return {
          onClick: withEventTracking(action.event, () => openModal(action)),
        };
      case CloseUseCreditActionActionType.close_use_credits:
        return {
          onClick: closeModal,
        };
      case ExitPartnershipBhActionActionType.unstamp_inactive_user:
        return {
          onClick: withEventTracking(action.event, exitHandler),
        };
      case LogoutBhActionActionType.logout:
        return {
          onClick: handleLogout,
        };
      default:
        return {};
    }
  }, [
    action,
    handleOnClickLink,
    openModal,
    closeModal,
    exitHandler,
    handleLogout,
  ]) as ActionButtonProps;
};
